import type { AppContext } from 'types/common'

import component from './Results.svelte'
import { graphql, type VendorSearch$result, type OrderByInstruction } from '$houdini'
import { stringToBoolean } from '@isoftdata/utility-string'

const vendorSearchResultColumnPropertyMap = {
	companyName: 'companyName',
	contactName: 'contactName',
	code: 'code',
	type: 'type',
	id: 'id',
	dateEntered: 'dateEntered',
	'billingAddress[address1]': 'street',
	'billingAddress[city]': 'city',
	'billingAddress[state]': 'state',
	'billingAddress[country]': 'country',
	'billingAddress[zip]': 'zip',
	email: 'email',
	webAddress: 'webAddress',
} as const

type VendorSearchResultColumnProperty = keyof typeof vendorSearchResultColumnPropertyMap
type VendorSearchResultColumnPropertyValue =
	(typeof vendorSearchResultColumnPropertyMap)[VendorSearchResultColumnProperty]
export type VendorSearchResult = VendorSearch$result['vendors']['items'][number]

function isAllowedVendorSearchResultColumn(value: string): value is VendorSearchResultColumnProperty {
	return value in vendorSearchResultColumnPropertyMap
}

export default function ({ stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.vendor-search.results',
		route: 'results',
		querystringParameters: [
			'pageNumber',
			'perPageCount',
			'orderByColumn',
			'orderByDirection',
			'keyword',
			'lastSavedTime',
			'showActiveVendorsOnly',
		],
		defaultParameters: {
			pageNumber: '1',
			perPageCount: '10',
			showActiveVendorsOnly: 'true',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(_data, parameters) {
			const pageNumber = parseInt(parameters.pageNumber, 10) || 1
			const pageSize = parseInt(parameters.perPageCount, 10) || 10
			const showActiveVendorsOnly = stringToBoolean(parameters.showActiveVendorsOnly)
			const formattedOrderByColumn: VendorSearchResultColumnPropertyValue | null = isAllowedVendorSearchResultColumn(
				parameters.orderByColumn,
			)
				? vendorSearchResultColumnPropertyMap[parameters.orderByColumn]
				: null
			const orderBy: OrderByInstruction | undefined = formattedOrderByColumn
				? {
						field: formattedOrderByColumn,
						direction: parameters.orderByDirection === 'DESC' ? 'DESC' : 'ASC',
					}
				: undefined

			const { data } = await vendorsQuery.fetch({
				variables: {
					filter: {
						keyword: parameters.keyword,
						active: showActiveVendorsOnly,
					},
					pagination: {
						pageNumber,
						pageSize,
					},
					orderBy: orderBy ? [orderBy] : undefined,
				},
			})

			if (!data) {
				throw new Error('No data returned from server')
			}

			return {
				vendors: data.vendors.items,
				showActiveVendorsOnly,
				perPageCount: data.vendors.pageInfo.pageSize.toString(),
				currentPageNumber: data.vendors.pageInfo.pageNumber,
				totalItemsCount: data.vendors.totalItems,
				orderByColumn: parameters.orderByColumn,
				orderByDirection: parameters.orderByDirection,
			}
		},
	})
	stateRouter.on('stateChangeStart', state => {
		if (!state.name.startsWith('app.vendor-search')) {
			sessionStorage.removeItem('selectedVendors')
		}
	})
}

const vendorsQuery = graphql(`
	query VendorSearch($filter: VendorFilter, $pagination: PaginationOptions, $orderBy: [OrderByInstruction!]) {
		vendors(filter: $filter, pagination: $pagination, orderBy: $orderBy) {
			pageInfo {
				pageNumber
				pageSize
				totalPages
			}
			totalItems
			items {
				companyName
				contactName
				code
				phoneNumber
				faxNumber
				mobilePhoneNumber
				type
				id
				dateEntered
				email
				webAddress
				active
				billingAddress {
					address1
					city
					country
					state
					zip
				}
				optionValues {
					option {
						id
						name
						type
						showInList
					}
					value
				}
			}
		}
	}
`)
